import Engine from "@drillflo/drillcore/lib/2d/Engine";
import View, { ViewOptions } from "@drillflo/drillcore/lib/2d/View";
import Drill from "@drillflo/drillcore/lib/Drill";
import { useCallback, useEffect, useRef, useState } from "react";

export const useDrill = (canvas: React.RefObject<HTMLCanvasElement>) => {
  const [viewOptions, setViewOptions] = useState<ViewOptions>({});
  /** Bumps when collaboration applies loadFromSerializedDrill (engine mutates in place). */
  const [collaborationSyncGeneration, setCollaborationSyncGeneration] =
    useState(0);
  const [view, setView] = useState<View | null>(null);
  const [drill, setDrill] = useState<Drill | null>(null);
  const [engine, setEngine] = useState<Engine | null>(null);

  // Keep viewOptions in a ref that's always up-to-date
  const viewOptionsRef = useRef(viewOptions);

  // Update the ref whenever viewOptions changes
  useEffect(() => {
    viewOptionsRef.current = viewOptions;
  }, [viewOptions]);

  // Persist display prefs: engine is authoritative (View + collaboration load).
  // Sync engine → customData so serialize() / Firestore carry hash, endzones, symbol.
  // Do not gate on viewOptions — after reload viewOptions is {} while engine is correct.
  useEffect(() => {
    if (!drill?.engine) {
      return;
    }

    const eng = drill.engine;
    const cd = drill.allCustomData;
    const optionsToSet: Record<string, string | boolean> = {};
    eng.setFieldYardHeight(52.5);
    const ht = String(eng.hashType);
    if (cd.hashType !== ht || cd.hashMarkType !== ht) {
      optionsToSet.hashType = ht;
      optionsToSet.hashMarkType = ht;
    }

    if (typeof eng.endZones === "boolean") {
      if (cd.endZones !== eng.endZones || cd.showEndzone !== eng.endZones) {
        optionsToSet.endZones = eng.endZones;
        optionsToSet.showEndzone = eng.endZones;
      }
    }

    const ps = String(eng.performerSymbolStyle);
    if (cd.performerSymbolStyle !== ps) {
      optionsToSet.performerSymbolStyle = ps;
    }

    if (Object.keys(optionsToSet).length > 0) {
      // Do not broadcast — this is consistency repair only; broadcasting here
      // races with collaboration load and can push default engine prefs to the server.
      drill.setMultipleCustomData(optionsToSet, false);
    }
  }, [viewOptions, drill, collaborationSyncGeneration]);

  useEffect(() => {
    if (!drill?.engine) {
      return;
    }
    const onCollaborationDrillReload = () => {
      setCollaborationSyncGeneration((g) => g + 1);
    };
    drill.engine.on(
      "drill-updated-from-collaboration",
      onCollaborationDrillReload,
    );
    return () => {
      drill.engine?.removeOn(
        "drill-updated-from-collaboration",
        onCollaborationDrillReload,
      );
    };
  }, [drill]);

  const forceViewRecreate = useCallback(() => {
    if (canvas.current) {
      const newView = new View(canvas.current, viewOptionsRef.current);
      setView((oldView) => {
        if (oldView) {
          oldView.destroy();
        }
        return newView;
      });
      setDrill(newView.getDrill());
      setEngine(newView.getEngine());
      newView.getDrill().draw();
    }
  }, [canvas]);

  useEffect(() => {
    forceViewRecreate();
  }, [canvas, forceViewRecreate]);

  const updateViewOptions = useCallback(
    async (newOptions: Partial<ViewOptions>) => {
      if (view) {
        const defined = Object.fromEntries(
          Object.entries(newOptions).filter(([, v]) => v !== undefined),
        ) as Partial<ViewOptions>;
        if (Object.keys(defined).length > 0) {
          view.updateOptions(defined);
          setViewOptions((prev) => ({ ...prev, ...defined }));
        }
        view.getDrill().broadcastDrillModifiedEvent();
      }
    },
    [view],
  );

  const draw = useCallback(async () => {
    if (view) {
      view.getDrill().draw();
    }
  }, [view]);

  return {
    view,
    drill,
    viewOptions,
    updateViewOptions,
    draw,
    engine,
    forceViewRecreate,
  };
};
